import { FC, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { captureException } from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Col, Flex, Input, Modal, Row, Space } from 'antd';
import { Tags as TagsIcon } from 'lucide-react';
import { TbArrowRight, TbCrosshair, TbTag, TbTags, TbUsers, TbX } from 'react-icons/tb';

import { workspaceWatchlistsQueryKeys } from '@/constants';
import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { usePlanUsageLimits } from '@/hooks/usePlanUsageLimits';
import { useCreateTag, useCreateWatchlist, useTags, useUpdateTag, useUpdateWatchlist } from '@/queries';
import { tagsService } from '@/services';
import { PlatformAlert } from '@/shared/components/alerts';
import { toast } from '@/shared/components/toasts/PlatformToast';
import { WorkspaceSubjectSelect } from '@/shared/components/workspace-resources';
import { useTeamStore, useWatchlistStore } from '@/stores';
import { SubjectReference, Tag, TagInput, Watchlist, WatchlistInput, WorkspacePrimitiveVisibility } from '@/types';

import { PlatformColorPicker, PlatformForm, PlatformSelect } from '../../../forms';
import LearnMoreFooter from '../../../layouts/footers/LearnMoreFooter';
import { EmptyView } from '../../../layouts/views/EmptySection';
import ResourceCreationFormLabel from '../components/ResourceCreationFormLabel';
import ResourceCreationModalContent from '../components/ResourceCreationModalContent';
import ResourceCreationModalTitle from '../components/ResourceCreationModalTitle';
import WatchlistCompanySelect from './WatchlistCompanySelect';

interface PrimitiveFormValues {
    description?: string;
    name: string;
    parentId?: string;
    teamId?: string;
}

interface BaseWorkspacePrimitiveEditorModalProps {
    errorMessage?: string;
    open: boolean;
    subjectsToAttach?: SubjectReference[];
    submitLabel?: ReactNode;
    successMessage?: string;
    onClose: () => void;
}

interface TagEditorModalProps extends BaseWorkspacePrimitiveEditorModalProps {
    availableTags?: Tag[];
    initialParentId?: string;
    kind: 'tag';
    record?: Tag;
    onCreated?: (tag: Tag) => Promise<void> | void;
    onUpdated?: (tag: Tag) => Promise<void> | void;
}

interface WatchlistEditorModalProps extends BaseWorkspacePrimitiveEditorModalProps {
    kind: 'watchlist';
    record?: Watchlist;
    onCreated?: (watchlist: Watchlist) => Promise<void> | void;
    onUpdated?: (watchlist: Watchlist) => Promise<void> | void;
}

export type WorkspacePrimitiveEditorModalProps = TagEditorModalProps | WatchlistEditorModalProps;

const visibilityOptions = ['Private', 'Shared', 'Team', 'Workspace'];
const formItemStyle = { marginBottom: '0.5rem' };

const WorkspacePrimitiveEditorModal: FC<WorkspacePrimitiveEditorModalProps> = (props): JSX.Element => {
    const { token } = useAntDesignTheme();
    const [form] = PlatformForm.useForm<PrimitiveFormValues>();
    const queryClient = useQueryClient();
    const subjectsToAttachRef = useRef<SubjectReference[]>([]);
    const [color, setColor] = useState<string>();
    const [companies, setCompanies] = useState<SubjectReference[]>([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        subjectsToAttachRef.current = props.subjectsToAttach ?? [];
    }, [props.subjectsToAttach]);
    const [subjects, setSubjects] = useState<SubjectReference[]>([]);
    const [visibility, setVisibility] = useState<WorkspacePrimitiveVisibility>(WorkspacePrimitiveVisibility.PRIVATE);
    const { fetchTeams, teams } = useTeamStore();
    const addWatchlistSubject = useWatchlistStore((state) => state.toggleSubject);
    const deleteWatchlist = useWatchlistStore((state) => state.deleteWatchlist);
    const tagsQuery = useTags();
    const createTag = useCreateTag();
    const updateTag = useUpdateTag();
    const createWatchlist = useCreateWatchlist();
    const updateWatchlist = useUpdateWatchlist();
    const editing = Boolean(props.record);
    const isTag = props.kind === 'tag';
    const noun = isTag ? 'Tag' : 'Watchlist';
    const { getUsageState } = usePlanUsageLimits();
    const usage = getUsageState(isTag ? 'tags' : 'watchlists');
    const creationLimitReached = !editing && usage.reached;
    const availableTags = props.kind === 'tag' ? props.availableTags : undefined;
    const initialParentId = props.kind === 'tag' ? props.initialParentId : undefined;
    const tagRecord = props.kind === 'tag' ? props.record : undefined;
    const watchlistRecord = props.kind === 'watchlist' ? props.record : undefined;
    const record = tagRecord ?? watchlistRecord;
    const tags = useMemo(
        () => (isTag ? (availableTags ?? tagsQuery.data ?? []) : []),
        [availableTags, isTag, tagsQuery.data],
    );
    const submitting =
        saving || createTag.isPending || updateTag.isPending || createWatchlist.isPending || updateWatchlist.isPending;

    useEffect(() => {
        if (props.open) {
            void fetchTeams();
        }
    }, [fetchTeams, props.open]);

    useEffect(() => {
        if (!props.open) {
            return;
        }

        const nextVisibility = record?.visibility ?? WorkspacePrimitiveVisibility.PRIVATE;

        setVisibility(nextVisibility);
        setColor(tagRecord?.color ?? undefined);
        setCompanies([]);
        setSubjects(subjectsToAttachRef.current);
        form.resetFields();
        form.setFieldsValue({
            description: watchlistRecord?.description ?? undefined,
            name: tagRecord?.name ?? watchlistRecord?.title ?? '',
            parentId: tagRecord?.parentId ?? initialParentId,
            teamId: record?.teamId ?? undefined,
        });
    }, [form, initialParentId, props.open, record, tagRecord, watchlistRecord]);

    const close = () => {
        if (!submitting) {
            props.onClose();
        }
    };

    const submit = async (values: PrimitiveFormValues) => {
        if (submitting || creationLimitReached) {
            return;
        }

        setSaving(true);

        try {
            if (props.kind === 'tag') {
                const input: TagInput = {
                    color: color ?? null,
                    name: values.name.trim(),
                    parentId: values.parentId ?? props.record?.parentId ?? initialParentId ?? null,
                    teamId: visibility === WorkspacePrimitiveVisibility.TEAM ? values.teamId : null,
                    visibility,
                };
                const tag = props.record
                    ? await updateTag.mutateAsync({ id: props.record.id, input })
                    : await createTag.mutateAsync(input);

                if (!props.record && subjects.length) {
                    try {
                        await Promise.all(subjects.map((subject) => tagsService.addAssignment(tag.id, subject)));
                        await queryClient.invalidateQueries({ queryKey: ['workspace-tags'] });
                    } catch (error) {
                        await tagsService.delete(tag.id).catch(captureException);
                        await queryClient.invalidateQueries({ queryKey: ['workspace-tags'] });
                        throw error;
                    }
                }

                if (props.record) {
                    await props.onUpdated?.(tag);
                } else {
                    await props.onCreated?.(tag);
                }
            } else {
                const input: WatchlistInput = {
                    description: values.description?.trim() || null,
                    teamId: visibility === WorkspacePrimitiveVisibility.TEAM ? values.teamId : null,
                    title: values.name.trim(),
                    visibility,
                };
                const watchlist = props.record
                    ? await updateWatchlist.mutateAsync({ id: props.record.id, input })
                    : await createWatchlist.mutateAsync(input);

                if (!props.record) {
                    const subjects = Array.from(
                        new Map(
                            [...(props.subjectsToAttach ?? []), ...companies].map((subject) => [
                                `${subject.subjectType}:${subject.subjectId}`,
                                subject,
                            ]),
                        ).values(),
                    );

                    try {
                        for (const subject of subjects) {
                            await addWatchlistSubject(watchlist.id, subject, false);
                        }
                        if (subjects.length) {
                            await queryClient.invalidateQueries({ queryKey: workspaceWatchlistsQueryKeys.all });
                        }
                    } catch (error) {
                        await deleteWatchlist(watchlist.id).catch(captureException);
                        throw error;
                    }
                }

                if (props.record) {
                    await props.onUpdated?.(watchlist);
                } else {
                    await props.onCreated?.(watchlist);
                }
            }

            toast.success(props.successMessage ?? `${noun} ${editing ? 'updated' : 'created'}.`);
            form.resetFields();
            props.onClose();
        } catch (error) {
            captureException(error);
            toast.error(props.errorMessage ?? `Unable to ${editing ? 'update' : 'create'} this ${noun.toLowerCase()}.`);
        } finally {
            setSaving(false);
        }
    };

    const parentTagOptions =
        props.kind === 'tag'
            ? tags
                  .filter((tag) => !tag.parentId && tag.id !== props.record?.id)
                  .map((tag) => ({ label: tag.name, value: tag.id }))
            : [];
    const nameFormItem = (
        <PlatformForm.Item
            label='Name'
            name='name'
            style={{ ...formItemStyle, flex: '1 1 auto', minWidth: 0 }}
            rules={[
                {
                    required: true,
                    whitespace: true,
                    message: `Enter a ${noun.toLowerCase()} name.`,
                },
                { max: isTag ? 120 : 250 },
            ]}
        >
            <Input maxLength={isTag ? 120 : 250} placeholder={`${noun} Name...`} />
        </PlatformForm.Item>
    );

    return (
        <Modal
            closable
            closeIcon={<TbX aria-label={`Close ${noun.toLowerCase()} editor`} size='1rem' />}
            destroyOnHidden
            footer={
                <Row align='middle'>
                    <Col flex='none'>
                        <LearnMoreFooter align='left' topic={isTag ? 'Tags' : 'Watchlists'} />
                    </Col>
                    <Col flex='auto' />
                    <Col flex='none'>
                        <Space size='small'>
                            <Button disabled={submitting} size='middle' onClick={close}>
                                Cancel
                            </Button>
                            <Button
                                disabled={submitting || creationLimitReached}
                                loading={submitting}
                                size='middle'
                                type='primary'
                                onClick={() => form.submit()}
                            >
                                {props.submitLabel ?? (editing ? 'Save Changes' : `Create ${noun}`)}
                                <TbArrowRight
                                    size='1rem'
                                    style={{ marginLeft: '0.25rem', verticalAlign: '-0.15rem' }}
                                />
                            </Button>
                        </Space>
                    </Col>
                </Row>
            }
            mask={false}
            maskClosable={!submitting}
            open={props.open}
            title={
                <ResourceCreationModalTitle
                    icon={
                        isTag ? (
                            <TagsIcon size='1rem' style={{ color: token.colorTextTertiary }} />
                        ) : (
                            <TbCrosshair size='1rem' style={{ color: token.colorTextTertiary }} />
                        )
                    }
                    setVisibility={(value) => setVisibility(value as WorkspacePrimitiveVisibility)}
                    title={`${editing ? 'Edit' : 'Create'} ${noun}`}
                    visibility={visibility}
                    visibilityOptions={visibilityOptions}
                />
            }
            width='min(36rem, calc(100vw - 2rem))'
            onCancel={close}
        >
            <ResourceCreationModalContent>
                {creationLimitReached ? (
                    <PlatformAlert
                        message={usage.reason}
                        showIcon
                        styles={{ marginBottom: '0.75rem' }}
                        type='warning'
                    />
                ) : null}
                <PlatformForm form={form} preserve={false} onFinish={submit}>
                    {isTag ? (
                        <Flex align='flex-end' gap={token.marginSM}>
                            <PlatformForm.Item style={{ ...formItemStyle, flex: 'none' }}>
                                <PlatformColorPicker
                                    allowClear
                                    value={color}
                                    onChange={(_, hex) => setColor(hex)}
                                    onClear={() => setColor(undefined)}
                                >
                                    <Button
                                        aria-label='Choose tag color'
                                        icon={<TbTag style={{ color: color ?? token.colorTextTertiary }} />}
                                    />
                                </PlatformColorPicker>
                            </PlatformForm.Item>
                            {nameFormItem}
                        </Flex>
                    ) : (
                        nameFormItem
                    )}
                    {props.kind === 'watchlist' ? (
                        <>
                            <PlatformForm.Item
                                label={
                                    <ResourceCreationFormLabel help='Describe the companies or resources this watchlist should track.'>
                                        Description
                                    </ResourceCreationFormLabel>
                                }
                                name='description'
                                style={formItemStyle}
                            >
                                <Input.TextArea
                                    maxLength={500}
                                    placeholder='What Should This Watchlist Track...'
                                    rows={3}
                                    showCount
                                    style={{ resize: 'none' }}
                                />
                            </PlatformForm.Item>
                            {!props.record ? (
                                <PlatformForm.Item
                                    label={
                                        <ResourceCreationFormLabel help='Search public and private company listings and add selections when the watchlist is created.'>
                                            Companies (Optional)
                                        </ResourceCreationFormLabel>
                                    }
                                    style={formItemStyle}
                                >
                                    <WatchlistCompanySelect
                                        disabled={submitting}
                                        value={companies}
                                        onChange={setCompanies}
                                    />
                                </PlatformForm.Item>
                            ) : null}
                        </>
                    ) : (
                        <>
                            {!props.record?.parentId && !initialParentId ? (
                                <PlatformForm.Item
                                    label={
                                        <ResourceCreationFormLabel help='Tags can be nested one level deep.'>
                                            Parent Tag
                                        </ResourceCreationFormLabel>
                                    }
                                    name='parentId'
                                    style={formItemStyle}
                                >
                                    <PlatformSelect
                                        allowClear
                                        notFoundContent={
                                            <EmptyView
                                                description='Create a top-level tag first, then return here to nest this tag beneath it.'
                                                height='9rem'
                                                icon={<TbTags size='1.25rem' />}
                                                label='No Parent Tags Available'
                                            />
                                        }
                                        optionFilterProp='label'
                                        options={parentTagOptions}
                                        placeholder='Optional — Create As A Top-Level Tag...'
                                        showSearch
                                    />
                                </PlatformForm.Item>
                            ) : null}
                            {!props.record ? (
                                <PlatformForm.Item
                                    label={
                                        <ResourceCreationFormLabel help='Choose companies or workspace resources that should receive this tag when it is created.'>
                                            Apply Tag To (Optional)
                                        </ResourceCreationFormLabel>
                                    }
                                    style={formItemStyle}
                                >
                                    <WorkspaceSubjectSelect
                                        disabled={submitting}
                                        value={subjects}
                                        onChange={setSubjects}
                                    />
                                </PlatformForm.Item>
                            ) : null}
                        </>
                    )}
                    {visibility === WorkspacePrimitiveVisibility.TEAM ? (
                        <PlatformForm.Item
                            label='Team'
                            name='teamId'
                            rules={[{ required: true, message: 'Choose the team that can access this item.' }]}
                            style={formItemStyle}
                        >
                            <PlatformSelect
                                notFoundContent={
                                    <EmptyView
                                        description='Create or join a team before limiting this item to team members.'
                                        height='9rem'
                                        icon={<TbUsers size='1.25rem' />}
                                        label='No Teams Available'
                                    />
                                }
                                optionFilterProp='label'
                                options={teams.map((team) => ({ label: team.name, value: team.id }))}
                                placeholder='Select A Team...'
                                showSearch
                            />
                        </PlatformForm.Item>
                    ) : null}
                </PlatformForm>
            </ResourceCreationModalContent>
        </Modal>
    );
};

export default WorkspacePrimitiveEditorModal;
